/**
 * KLEOS Gateway API Contracts — the wire shapes of the KLEOS-facing facade
 * (`/internal/kleos/runs`).
 *
 * These are NOT the ledger's own contracts. `api/agent-runs` describes
 * `/internal/agent-runs`, which is enveloped (`{ message, data }`), answers
 * `409` on a repeated fingerprint, and is gated by the shared
 * `INTERNAL_API_KEY`. KLEOS speaks a deliberately smaller, flat dialect over
 * the same ledger: a brief and a client key in, a run id and a lowercase
 * status word out. The two shapes are kept apart on purpose — bending the
 * ledger route into KLEOS's shape would change a surface six CI workflows and
 * three task definitions already parse.
 *
 * The lowercase status vocabulary here is the contract KLEOS decides
 * terminality from. The raw ledger enum rides alongside as `ledgerStatus`,
 * which KLEOS carries but never interprets.
 *
 * Plan: docs/_archive/2026-10-17-plans-sweep/plans/2026-09-03-0051-feat-sj-kleos-gateway-facade-plan.md
 */

import { z } from 'zod'
import { AgentRunStatusSchema } from '../../schemas/entities/agent-run'

// POST /internal/kleos/runs

/**
 * `brief` is the whole ask, as prose — the facade stores it as
 * `brief.text` and the executor uses it verbatim as the prompt.
 *
 * `clientKey` is KLEOS's own idempotency key. It does NOT land on the run
 * verbatim: the facade stores `<kleosAgent>:<clientKey>` as `fingerprint`, so
 * `@@unique([lane, fingerprint])` is per agent and two KLEOS agents sharing a
 * key cannot collide onto one run. The cap here is that composite, not a taste
 * judgement — the fingerprint column is `VarChar(191)` and an agent label is at
 * most 63 chars (the seeder's `LABEL_PATTERN`), which leaves `191 - 63 - 1 =
 * 127` for the key. An over-long key fails at the edge with a `400` rather than
 * at the write with a truncation that would break idempotency silently.
 */
/**
 * Where a trigger-originated brief came from, as FIELDS rather than prose — the
 * executor acts on these and never on the brief text (KTD5; the argument lives
 * on `renderSuppliedContext` in `services/ai-agents/jobs/partner-run-commit.ts`).
 *
 * `channelId` and `ts` are the trigger's identity (they are what the client key
 * is derived from), so they are required together. The other three describe it
 * and are `nullish`, not `optional`: the sender is a different repo, and a
 * top-level message has no thread root to send — `null` there must validate
 * rather than 400 a real trigger.
 *
 * The bounds are the columns and surfaces these land in, not taste:
 * `School.slackChannelId` is `VarChar(32)`, a Slack `ts` is `<10 digits>.<6>`,
 * and a permalink is a URL we only ever echo.
 */
export const KleosRunTriggerSchema = z.object({
  /** The Slack channel the reacted message is in — matched against `School.slackChannelId`. */
  channelId: z.string().min(1).max(32),
  /** The reacted message's own `ts`. */
  ts: z.string().min(1).max(32),
  /** The thread root's `ts`, or null for a top-level message. */
  threadTs: z.string().min(1).max(32).nullish(),
  /** Permalink back to the reacted message, echoed onto the ticket. */
  permalink: z.string().min(1).max(512).nullish(),
  /** The Slack user id that reacted or mentioned. Recorded, never rendered to a customer. */
  reactorId: z.string().min(1).max(32).nullish()
})
export type KleosRunTrigger = z.infer<typeof KleosRunTriggerSchema>

/**
 * `trigger` is OPTIONAL and its absence is not an error: every caller that
 * existed before the Slack-triage path sends `{ brief, clientKey }` alone, and
 * a brief with no trigger runs exactly as it always has — persona output
 * straight through to the summary, no school lookup and no Linear write.
 */
export const KleosCreateRunRequestSchema = z.object({
  brief: z.string().trim().min(1).max(8000),
  clientKey: z.string().min(1).max(127),
  trigger: KleosRunTriggerSchema.optional()
})
export type KleosCreateRunRequest = z.infer<typeof KleosCreateRunRequestSchema>

/**
 * The kinds of KLEOS run SJ recognizes by a directive on the brief's first
 * non-empty line (SCH-2095 KTD3). A brief with no directive has no kind and
 * runs as a plain persona run, exactly as before.
 */
export const KleosRunKindSchema = z.enum(['customer-pulse'])
export type KleosRunKind = z.infer<typeof KleosRunKindSchema>

/**
 * The `brief` JSON the facade stores on a `kleos`-lane `AgentRun`. NOT a wire
 * shape: KLEOS never sees it, and the request above is unchanged by it.
 *
 * `kind` and `pulseSchoolTerm` are parsed from the brief TEXT at intake and are
 * present together or not at all. They travel in the text rather than as a
 * request field because KLEOS retry and heartbeat re-issue re-send only the
 * brief, so a text directive survives them with no change to the contract
 * (SCH-2095 KTD3). `pulseSchoolTerm` is the raw school term as typed (a name,
 * slug or id); resolving it to a school is the executor's job, not intake's.
 * `pulseSchoolUpdate` is present, and `true`, only when the directive carried
 * the `school-update` option (SCH-2174 KTD1); absent means snapshot only.
 */
export const KleosStoredBriefSchema = z.object({
  text: z.string(),
  origin: z.literal('kleos'),
  kleosAgent: z.string(),
  trigger: KleosRunTriggerSchema.optional(),
  kind: KleosRunKindSchema.optional(),
  pulseSchoolTerm: z.string().min(1).optional(),
  pulseSchoolUpdate: z.literal(true).optional()
})
export type KleosStoredBrief = z.infer<typeof KleosStoredBriefSchema>

/**
 * `kleos.customer_pulse` — the customer-pulse kill switch (SCH-2095 KTD9), a
 * `SYSTEM_CONFIG` row with a null `schoolId`.
 *
 * The defaults ARE the absent-row answer: off, nobody allowed. `allowedAgents`
 * holds KLEOS gateway key labels, the same label
 * `verifyKleosServiceKeyMiddleware` resolves and the facade stores as
 * `brief.kleosAgent`. `maxConcurrent` caps pulse VMs running at once.
 */
export const KleosCustomerPulseConfigSchema = z.object({
  enabled: z.boolean().default(false),
  allowedAgents: z.array(z.string().min(1)).default([]),
  maxConcurrent: z.number().int().min(0).default(2)
})
export type KleosCustomerPulseConfig = z.infer<typeof KleosCustomerPulseConfigSchema>

/**
 * What KLEOS is told a run's state is.
 *
 * Lowercase and deliberately not the ledger enum: KLEOS decides "finished"
 * from a word list, so the two systems agree on terminality
 * (`completed` / `failed` / `rejected`) without KLEOS having to track the
 * ledger's lifecycle. The ledger -> KLEOS map is one function,
 * `services/agent-runs/kleos-facing.ts`, so the vocabulary cannot drift
 * between a facade response and a completion webhook.
 */
export const KleosRunStatusSchema = z.enum([
  'assessing',
  'queued',
  'running',
  'blocked',
  'completed',
  'failed',
  'rejected',
  'parked'
])
export type KleosRunStatus = z.infer<typeof KleosRunStatusSchema>

/**
 * `201` for a fresh run, `200` with `existing: true` for a repeated client
 * key — never `409`. KLEOS reads a repeat from either signal, so both are
 * sent.
 *
 * `monitorUrl` is OPTIONAL AND OMITTED, never `null`: it is only emitted when
 * it can be built as an absolute `https` URL with no embedded credentials.
 */
export const KleosCreateRunResponseSchema = z.object({
  /** The ledger's public id, `run_…`. */
  runId: z.string(),
  status: KleosRunStatusSchema,
  monitorUrl: z.string().url().optional(),
  /**
   * Only a repeat can carry one — a fresh run has written no headline yet — and
   * like `monitorUrl` it is omitted rather than sent as `null`.
   */
  summary: z.string().optional(),
  /** The raw ledger status, carried opaquely by KLEOS. */
  ledgerStatus: AgentRunStatusSchema,
  /** Present, and always `true`, only when the client key resolved to a run that already existed. */
  existing: z.literal(true).optional()
})
export type KleosCreateRunResponse = z.infer<typeof KleosCreateRunResponseSchema>

// GET /internal/kleos/runs/:runId

/**
 * The status read. Flat, and scoped to the calling agent's own runs — a run
 * in another lane, or another KLEOS agent's run, answers `404` rather than
 * appearing here.
 *
 * `summary` is a summary and never a transcript: it is signed and shipped to
 * a vendor and from there into a Slack surface. Like `monitorUrl` it is
 * omitted rather than sent as `null`.
 */
export const KleosGetRunResponseSchema = z.object({
  status: KleosRunStatusSchema,
  monitorUrl: z.string().url().optional(),
  summary: z.string().optional(),
  ledgerStatus: AgentRunStatusSchema
})
export type KleosGetRunResponse = z.infer<typeof KleosGetRunResponseSchema>

// POST /internal/kleos/runs/:runId/not-an-issue

/**
 * A teammate clicked "Not an issue" on a card whose triage already filed or
 * updated a ticket (customer issue loop, KTD13). No body: the run id names the
 * ticket, and the comment is fixed text, so nothing KLEOS sends reaches Linear.
 *
 * `commented` is `true` when THIS call wrote the comment and `false` when an
 * earlier call for the same run already had. Both are `200`; a repeat is an
 * answer, not an error, and never a second comment.
 */
export const KleosNotAnIssueResponseSchema = z.object({
  /** The run's public id, `run_…`, echoed. */
  runId: z.string(),
  /** The ticket the note is on, `SCH-NNNN`. */
  issueKey: z.string(),
  commented: z.boolean()
})
export type KleosNotAnIssueResponse = z.infer<typeof KleosNotAnIssueResponseSchema>

// GET /internal/kleos/runs/school-names?channelId=…

/**
 * `channelId` is optional: without it the route answers the active-name list
 * alone. The bound is `School.slackChannelId`'s column, as on the trigger.
 */
export const KleosSchoolNamesQuerySchema = z.object({
  channelId: z.string().trim().min(1).max(32).optional()
})
export type KleosSchoolNamesQuery = z.infer<typeof KleosSchoolNamesQuerySchema>

/**
 * School names for the review card and the draft filter (customer issue loop,
 * KTD15). KLEOS has no school mapping of its own and caches this for an hour.
 *
 * `channelSchool` is the one school whose `slackChannelId` is the asked
 * channel, or `null` when none or several are — the column is not unique, and
 * naming one of two would be a guess. `activeSchoolNames` is every active
 * school's name (not soft-deleted, holding a TRIAL or PAYING subscription),
 * sorted; the draft filter refuses any of them other than the thread's own.
 */
export const KleosSchoolNamesResponseSchema = z.object({
  channelSchool: z.object({ name: z.string(), publicId: z.string() }).nullable(),
  activeSchoolNames: z.array(z.string())
})
export type KleosSchoolNamesResponse = z.infer<typeof KleosSchoolNamesResponseSchema>

// School Journey -> KLEOS: `X-Kleos-Event: issue.resolved`

/** The `X-Kleos-Event` header value, and the body's own `event` field. */
export const KLEOS_ISSUE_RESOLVED_EVENT = 'issue.resolved'

/**
 * What happened to the Linear issue behind a triage run (customer issue loop,
 * KTD8, KTD9). `reopened` is only ever sent after a `done` for the same run.
 */
export const KleosIssueResolvedStateSchema = z.enum(['done', 'canceled', 'duplicate', 'reopened'])
export type KleosIssueResolvedState = z.infer<typeof KleosIssueResolvedStateSchema>

/**
 * The signed `issue.resolved` body, one per run per state change (KTD9).
 *
 * Same webhook URL, secret and signature scheme as `run.completed`. `runId` and
 * `clientKey` are the values `run.completed` sends for the same run, so KLEOS
 * correlates the two on the same keys. `issueKey` is the issue whose state this
 * is (after a `duplicate`, the surviving ticket); `survivorIssueKey` is present
 * exactly when `state` is `duplicate`. KLEOS does not store either key.
 *
 * `sentAt` is inside the signed bytes so a captured request cannot be replayed
 * later: KLEOS refuses one more than 10 minutes old or 1 minute in the future.
 */
export const KleosIssueResolvedBodySchema = z
  .object({
    event: z.literal(KLEOS_ISSUE_RESOLVED_EVENT),
    runId: z.string().min(1),
    clientKey: z.string(),
    state: KleosIssueResolvedStateSchema,
    issueKey: z.string().min(1),
    survivorIssueKey: z.string().min(1).optional(),
    sentAt: z.string().datetime()
  })
  .refine(body => (body.state === 'duplicate') === (body.survivorIssueKey !== undefined), {
    message: 'survivorIssueKey is required for state duplicate and absent otherwise',
    path: ['survivorIssueKey']
  })
export type KleosIssueResolvedBody = z.infer<typeof KleosIssueResolvedBodySchema>
